import { useState, useEffect, useCallback, useRef } from 'react';
import { priceService } from '../services/priceService';
import { useStore } from '../../../store/useStore';
import { Categoria } from '../services/priceService';

interface UsePromotionPaginationOptions {
    selectedCategory: 'all' | number;
    searchTerm: string;
    barcode?: string;
    internalCode?: string;
    somenteComPromocoes?: boolean;
    tiposUnidade?: string[]; // Tipos de unidade selecionados (array de siglas: ['UN', 'KG', etc.])
    categorias: Categoria[];
    pageSize?: number; // Tamanho da página (padrão 50)
    onLoadComplete?: (count: number) => void;
    autoSearch?: boolean; // Se false, não busca automaticamente quando searchTerm mudar
}

export const usePromotionPagination = ({
    selectedCategory,
    searchTerm,
    barcode = '',
    internalCode = '',
    somenteComPromocoes = false,
    tiposUnidade,
    categorias,
    pageSize = 50,
    onLoadComplete,
    autoSearch = true
}: UsePromotionPaginationOptions) => {
    const { promotions, addPromotion, updatePromotion, deletePromotion } = useStore();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [produtosBuscados, setProdutosBuscados] = useState<number[]>([]);
    const [vinculacoesProdutos, setVinculacoesProdutos] = useState<Map<number, boolean>>(new Map()); // Mapa de produtoId -> vinculadoATela
    const [currentPage, setCurrentPage] = useState(1);
    const [hasNextPage, setHasNextPage] = useState(false);
    const [hasPreviousPage, setHasPreviousPage] = useState(false);
    
    const isProcessingRef = useRef(false);
    const lastCategoryRef = useRef<'all' | number | null>(null);
    const lastSearchTermRef = useRef<string>('');
    const lastBarcodeRef = useRef<string>('');
    const lastInternalCodeRef = useRef<string>('');
    const lastPageSizeRef = useRef<number>(pageSize);
    const lastSomenteComPromocoesRef = useRef<boolean>(somenteComPromocoes);
    const lastTiposUnidadeRef = useRef<string[] | undefined>(tiposUnidade);
    const pageHistoryRef = useRef<number[]>([1]); // Histórico de páginas visitadas
    const shouldClearProductsRef = useRef(false); // Flag para limpar produtos apenas quando categoria/busca mudar
    
    // Cache de páginas: Map<chaveCache, Map<numeroPagina, {produtoIds: number[], hasNext: boolean}>>
    const pageCacheRef = useRef<Map<string, Map<number, { produtoIds: number[]; hasNext: boolean }>>>(new Map());
    
    // Função para gerar chave única do cache baseada nos filtros
    const getCacheKey = useCallback(() => {
        const tiposKey = tiposUnidade && tiposUnidade.length > 0 
            ? tiposUnidade.sort().join(',') 
            : 'all';
        return `${selectedCategory}_${searchTerm}_${barcode}_${internalCode}_${somenteComPromocoes}_${tiposKey}_${pageSize}`;
    }, [selectedCategory, searchTerm, barcode, internalCode, somenteComPromocoes, tiposUnidade, pageSize]);

    // Função para restaurar produtos do cache no store
    const restaurarProdutosDoCache = useCallback((produtoIds: number[], nomeCategoria: string) => {
        const storeState = useStore.getState();
        const currentPromotions = storeState.promotions;
        
        // Filtrar apenas os produtos que estão no cache e na categoria correta
        const produtosCache = currentPromotions.filter(p => 
            produtoIds.includes(p.id) && p.category === nomeCategoria
        );
        
        setProdutosBuscados(produtoIds);
        return produtosCache.length === produtoIds.length; // Retorna true se todos os produtos estão no store
    }, []);


    // Função para buscar produtos de uma página específica
    const buscarProdutos = useCallback(async (page: number, forceRefresh = false) => {
        // Se forceRefresh for true, ignorar o flag de processamento para garantir que a busca seja executada
        if (!forceRefresh && isProcessingRef.current) return;
        
        // Sempre pegar os valores atuais diretamente das props (não usar closure)
        // Isso garante que sempre usamos os valores mais recentes
        const currentBarcode = barcode.trim();
        const currentInternalCode = internalCode.trim();
        const currentSearchTerm = searchTerm.trim();
        const currentSelectedCategory = selectedCategory;
        
        // Verificar se é busca pura (código de barra ou código interno)
        const hasBarcode = currentBarcode !== '';
        const hasInternalCode = currentInternalCode !== '';
        const isPureSearch = hasBarcode || hasInternalCode;
        
        let nomeCategoria: string;
        if (selectedCategory !== 'all') {
            const categoriaSelecionada = categorias.find(c => c.secaoId === selectedCategory);
            nomeCategoria = categoriaSelecionada?.secao || 'Categoria';
        } else {
            nomeCategoria = 'Geral';
        }
        
        const cacheKey = getCacheKey();
        
        // Para buscas puras (código de barra/código interno), SEMPRE limpar o cache da página
        // Isso garante que mesmo buscando o mesmo código duas vezes, sempre faz nova requisição
        if (isPureSearch) {
            const cache = pageCacheRef.current.get(cacheKey);
            if (cache) {
                cache.delete(page);
            }
        }
        
        const cache = pageCacheRef.current.get(cacheKey);
        const cachedPage = cache?.get(page);
        
        // Verificar se a página está no cache e não é um refresh forçado
        // IMPORTANTE: Para buscas puras, o cache já foi limpo acima, então nunca entrará aqui
        if (!forceRefresh && cachedPage && !isPureSearch) {
            // Tentar restaurar do cache apenas para buscas normais
            const todosProdutosNoStore = restaurarProdutosDoCache(cachedPage.produtoIds, nomeCategoria);
            
            if (todosProdutosNoStore) {
                // Todos os produtos estão no store, usar cache
                setProdutosBuscados(cachedPage.produtoIds);
                setHasNextPage(cachedPage.hasNext);
                setHasPreviousPage(page > 1);
                onLoadComplete?.(cachedPage.produtoIds.length);
                return; // Não fazer requisição
            }
            // Se não estiverem todos no store, fazer requisição normalmente
        }
        
        // NOTA: Sempre fazer a busca na API quando mudar pageSize
        // Mas vamos otimizar o processamento reutilizando produtos já no store
        
        isProcessingRef.current = true;
        setLoading(true);
        setError(null);

        try {
            let nomeCategoria: string;
            // Criar objeto de parâmetros limpo para garantir que não há valores residuais
            const apiParams: any = {};

            // Se código de barra OU código interno estiver preenchido, fazer busca pura (ignorar outros filtros)
            // Exceto se houver também um nome digitado
            const hasSearchTerm = currentSearchTerm !== '';

            if (isPureSearch) {
                // Busca por código de barra e/ou código interno - APENAS esses parâmetros, sem page/pageSize
                
                // Adicionar código de barra se preenchido
                if (hasBarcode) {
                    apiParams.codigoBarras = currentBarcode;
                }
                
                // Adicionar código interno se preenchido
                if (hasInternalCode) {
                    const produtoId = parseInt(currentInternalCode, 10);
                    if (!isNaN(produtoId) && produtoId > 0) {
                        apiParams.produtoId = produtoId;
                    }
                }
                
                // Se houver também pesquisa por nome, adicionar
                if (hasSearchTerm) {
                    apiParams.pesquisa = currentSearchTerm;
                }
                
                // Adicionar tipos de unidade se selecionados (filtro opcional)
                // Os tipos serão enviados como múltiplos parâmetros na query string
                if (tiposUnidade && tiposUnidade.length > 0) {
                    apiParams.tiposUnidade = tiposUnidade.map(t => t.toUpperCase()); // Garantir maiúsculo
                }
                
                // NÃO adicionar page, pageSize, secaoId ou outros filtros para buscas puras
                // Apenas os parâmetros específicos acima
                
                // Não aplicar outros filtros quando há código de barra ou código interno
                nomeCategoria = 'Geral'; // Categoria será definida pelo produto retornado
            } else {
                // Busca normal - aplicar todos os filtros incluindo paginação
                apiParams.page = page;
                apiParams.pageSize = pageSize;
                // Busca normal - aplicar todos os filtros
                if (currentSelectedCategory !== 'all') {
                    apiParams.secaoId = currentSelectedCategory;
                    const categoriaSelecionada = categorias.find(c => c.secaoId === currentSelectedCategory);
                    nomeCategoria = categoriaSelecionada?.secao || 'Categoria';
                } else {
                    nomeCategoria = 'Geral'; // Busca geral sem filtro de categoria
                }

                // Apenas searchTerm na busca normal (código de barra e código interno não são usados aqui)
                if (hasSearchTerm) {
                    apiParams.pesquisa = currentSearchTerm;
                }

                if (somenteComPromocoes) {
                    apiParams.somenteComPromocoes = true;
                }

                // Adicionar tipos de unidade se selecionados (filtro opcional)
                // Os tipos serão enviados como múltiplos parâmetros na query string
                if (tiposUnidade && tiposUnidade.length > 0) {
                    apiParams.tiposUnidade = tiposUnidade.map(t => t.toUpperCase()); // Garantir maiúsculo
                }
            }

            // Buscar página específica
            const result = await priceService.listarPrecos(apiParams);
            const items = result.items || [];

            // Processar produtos e adicionar ao store
            const today = new Date();
            const nextWeek = new Date(today);
            nextWeek.setDate(today.getDate() + 7);
            const validUntil = nextWeek.toISOString().split('T')[0];

            const produtoIds: number[] = [];
            const storeState = useStore.getState();

            // Limpar produtos apenas quando categoria ou busca mudar (não ao mudar de página)
            // Para buscas puras (código de barra/código interno), limpar todos os produtos exceto "Frente de Caixa"
            if (shouldClearProductsRef.current) {
                const produtosParaRemover = storeState.promotions
                    .filter(p => {
                        if (p.category === 'Frente de Caixa') return false;
                        // Para buscas puras, limpar todos os produtos (exceto Frente de Caixa)
                        // Para buscas normais, manter produtos da categoria atual
                        if (isPureSearch) {
                            return true; // Limpar todos na busca pura
                        }
                        if (p.category === nomeCategoria) return false;
                        return true;
                    })
                    .map(p => p.id);

                produtosParaRemover.forEach(id => {
                    storeState.deletePromotion(id);
                });
                shouldClearProductsRef.current = false;
            }
            
            // Obter lista atualizada de produtos APÓS a limpeza
            const currentPromotions = storeState.promotions;

            // Se não houver itens na resposta, limpar produtos
            if (items.length === 0) {
                if (isPureSearch) {
                    // Para busca pura, limpar todos exceto "Frente de Caixa"
                    const produtosParaLimpar = storeState.promotions
                        .filter(p => p.category !== 'Frente de Caixa')
                        .map(p => p.id);
                    produtosParaLimpar.forEach(id => {
                        storeState.deletePromotion(id);
                    });
                } else {
                    // Para busca normal, limpar apenas produtos da categoria atual
                    const produtosCategoriaAtual = storeState.promotions
                        .filter(p => p.category === nomeCategoria)
                        .map(p => p.id);
                    produtosCategoriaAtual.forEach(id => {
                        storeState.deletePromotion(id);
                    });
                }
            }

            // Mapa para armazenar vinculacoes dos produtos desta busca
            const novasVinculacoes = new Map<number, boolean>();

            // Processar cada item
            // IMPORTANTE: Sempre adicionar produtoId à lista primeiro, antes de qualquer verificação
            items.forEach((item: any) => {
                const produtoId = item.produtoId;
                
                // SEMPRE adicionar produtoId à lista primeiro
                // Isso garante que mesmo se houver algum problema no processamento,
                // o produto ainda será incluído na lista de produtos buscados
                produtoIds.push(produtoId);
                
                // Armazenar valor de vinculadoATela do JSON da API
                const vinculadoATela = item.vinculadoATela === true || item.vinculadoATela === 'true';
                novasVinculacoes.set(produtoId, vinculadoATela);

                // Buscar o nome da seção baseado no secaoId do produto que vem diretamente do JSON da API
                let categoriaProduto = 'Geral';
                
                // Pegar secaoId do item (pode vir como secaoId, secao_id, ou outro formato)
                const secaoIdRaw = item.secaoId || item.secao_id || item.secaoId;
                
                // Converter secaoId para número (pode vir como string ou number do JSON)
                const secaoIdProduto = secaoIdRaw !== undefined && secaoIdRaw !== null 
                    ? Number(secaoIdRaw) 
                    : null;
                
                // Se temos um secaoId válido e categorias carregadas, buscar o nome da seção
                if (secaoIdProduto !== null && !isNaN(secaoIdProduto) && secaoIdProduto > 0) {
                    if (categorias.length > 0) {
                        // Buscar o nome da seção nas categorias usando o secaoId retornado no JSON do produto
                        // Comparar ambos como números para garantir match correto
                        const categoriaEncontrada = categorias.find(c => {
                            const categoriaSecaoId = Number(c.secaoId);
                            return categoriaSecaoId === secaoIdProduto;
                        });
                        
                        if (categoriaEncontrada && categoriaEncontrada.secao && categoriaEncontrada.secao.trim() !== '') {
                            categoriaProduto = categoriaEncontrada.secao;
                        }
                    }
                }

                // Obter referência atualizada do store para verificar se produto existe
                const storeStateUpdated = useStore.getState();
                const existingPromo = storeStateUpdated.promotions.find(p => p.id === produtoId);

                // Para buscas puras, sempre processar os produtos para garantir que estão atualizados
                // Para buscas normais, otimizar reutilizando produtos já carregados
                if (!isPureSearch && existingPromo && existingPromo.category === categoriaProduto) {
                    // Produto já existe no store com categoria correta - reutilizar
                    // Não fazer update para evitar processamento desnecessário
                    // produtoId já foi adicionado acima, então apenas retornar
                    return;
                }

                // Produto não existe ou categoria mudou - processar normalmente
                const preco = Number(item.vlPrecoNormal || item.preco || 0);
                const precoPromocional = Number(item.vlPreco || item.precoPromocional || 0);
                const emPromocao = item.promocao === true || item.promocao === 'true' ||
                    item.emPromocao === true || item.emPromocao === 'true';
                const nome = item.nome || item.descricao || `Produto ${produtoId}`;

                // categoriaProduto já foi calculado acima, reutilizar

                const promoData: any = {
                    name: nome,
                    price: emPromocao && precoPromocional > 0 ? precoPromocional : (preco > 0 ? preco : 0),
                    originalPrice: emPromocao && preco > 0 ? preco : undefined,
                    unit: item.tipoUnidade || item.unidade || 'UN',
                    category: categoriaProduto, // Usar a categoria real do produto, não do filtro
                    validUntil: validUntil,
                    description: `Código: ${produtoId}${emPromocao ? ' - EM PROMOÇÃO' : ''}`,
                    isVideoPromotion: false,
                    hasAudio: true,
                    autoplay: true,
                    loop: true,
                    createdAt: new Date().toISOString()
                };

                // Usar a mesma referência do store que já obtivemos
                if (existingPromo) {
                    // Produto existe mas categoria mudou - atualizar
                    storeStateUpdated.updatePromotion(produtoId, promoData);
                } else {
                    // Produto novo - adicionar
                    storeStateUpdated.addPromotion(promoData, produtoId);
                }
            });

            // Atualizar mapa de vinculacoes com os novos valores
            setVinculacoesProdutos(prev => {
                const updated = new Map(prev);
                novasVinculacoes.forEach((value, key) => {
                    updated.set(key, value);
                });
                return updated;
            });
            
            setProdutosBuscados(produtoIds);
            setHasNextPage(result.hasNext || false);
            setHasPreviousPage(page > 1);
            onLoadComplete?.(items.length);
            
            // NUNCA salvar no cache para buscas puras (código de barra/código interno)
            // Isso garante que sempre faz nova requisição, mesmo com valores iguais
            // Buscas puras sempre devem fazer requisição para garantir dados atualizados
            if (!isPureSearch) {
                // Recriar cache key para garantir que está atualizado
                const finalCacheKey = getCacheKey();
                const finalCache = pageCacheRef.current.get(finalCacheKey);
                if (!finalCache) {
                    pageCacheRef.current.set(finalCacheKey, new Map());
                }
                const currentCache = pageCacheRef.current.get(finalCacheKey)!;
                currentCache.set(page, {
                    produtoIds: [...produtoIds], // Copiar array para evitar referência
                    hasNext: result.hasNext || false
                });
            } else {
                // Para buscas puras, garantir que o cache está limpo
                const finalCacheKey = getCacheKey();
                const finalCache = pageCacheRef.current.get(finalCacheKey);
                if (finalCache) {
                    finalCache.delete(page);
                }
            }

        } catch (err) {
            const errorMessage = err instanceof Error ? err.message : 'Erro ao carregar produtos';
            setError(errorMessage);
        } finally {
            setLoading(false);
            isProcessingRef.current = false;
        }
    }, [selectedCategory, searchTerm, barcode, internalCode, somenteComPromocoes, tiposUnidade, categorias, pageSize, onLoadComplete, getCacheKey, restaurarProdutosDoCache]);

    // Função para buscar manualmente (usada pelo botão de busca)
    const triggerSearch = useCallback(() => {
        // Resetar flag de processamento para garantir que a busca possa ser executada
        isProcessingRef.current = false;
        
        // Sempre limpar produtos e cache quando buscar manualmente
        // Isso é especialmente importante para buscas puras (código de barra/código interno)
        // onde mesmo buscando o mesmo valor, queremos uma nova busca
        shouldClearProductsRef.current = true;
        setCurrentPage(1);
        pageHistoryRef.current = [1];
        
        // Atualizar referências ANTES de limpar cache para garantir que a nova busca use os valores corretos
        lastCategoryRef.current = selectedCategory;
        lastSearchTermRef.current = searchTerm;
        lastBarcodeRef.current = barcode;
        lastInternalCodeRef.current = internalCode;
        
        // Limpar cache antigo completamente - especialmente importante para buscas repetidas
        pageCacheRef.current.clear();
        
        // Forçar refresh para garantir que sempre busca na API, mesmo com valores iguais
        // O forceRefresh=true garante que ignora qualquer cache e sempre faz requisição
        buscarProdutos(1, true);
    }, [selectedCategory, searchTerm, barcode, internalCode, buscarProdutos]);

    // Buscar produtos quando categoria mudar (mas não quando searchTerm mudar se autoSearch for false)
    // OTIMIZADO: Removido buscarProdutos das dependências para evitar buscas desnecessárias
    // quando apenas a referência da função muda (ex: quando categorias é recriado)
    useEffect(() => {
        // Se já está processando, não fazer nada
        if (isProcessingRef.current) {
            return;
        }

        // Primeira vez - inicializar e buscar sempre (busca geral se 'all')
        if (lastCategoryRef.current === null) {
            // Atualizar referências primeiro
            lastCategoryRef.current = selectedCategory;
            lastSearchTermRef.current = searchTerm;
            lastBarcodeRef.current = barcode;
            lastInternalCodeRef.current = internalCode;
            lastPageSizeRef.current = pageSize;
            lastSomenteComPromocoesRef.current = somenteComPromocoes;
            lastTiposUnidadeRef.current = tiposUnidade;
            // Sempre buscar na primeira vez (busca geral se selectedCategory for 'all')
            shouldClearProductsRef.current = true;
            setCurrentPage(1);
            pageHistoryRef.current = [1];
            // Limpar qualquer cache existente na primeira carga
            pageCacheRef.current.clear();
            buscarProdutos(1);
        } else {
            // Verificar se categoria mudou
            const categoryChanged = lastCategoryRef.current !== selectedCategory;
            const searchChanged = lastSearchTermRef.current !== searchTerm;
            const barcodeChanged = lastBarcodeRef.current !== barcode;
            const internalCodeChanged = lastInternalCodeRef.current !== internalCode;
            const pageSizeChanged = lastPageSizeRef.current !== pageSize;
            const somenteComPromocoesChanged = lastSomenteComPromocoesRef.current !== somenteComPromocoes;
            // Comparar arrays de tipos de unidade
            const tiposUnidadeChanged = JSON.stringify(lastTiposUnidadeRef.current?.sort()) !== JSON.stringify(tiposUnidade?.sort());
            
            // Se nada mudou, não fazer nada
            if (!categoryChanged && !searchChanged && !barcodeChanged && !internalCodeChanged && !pageSizeChanged && !somenteComPromocoesChanged && !tiposUnidadeChanged) {
                return;
            }

            // Se apenas searchTerm, barcode ou internalCode mudou mas autoSearch é false, apenas atualizar a referência sem buscar
            // Mas se somenteComPromocoes ou tiposUnidade mudou, sempre buscar
            if ((searchChanged || barcodeChanged || internalCodeChanged) && !categoryChanged && !pageSizeChanged && !somenteComPromocoesChanged && !tiposUnidadeChanged && !autoSearch) {
                lastSearchTermRef.current = searchTerm;
                lastBarcodeRef.current = barcode;
                lastInternalCodeRef.current = internalCode;
                return;
            }

            // Se categoria ou pageSize mudou (ou ambos mudaram), buscar
            // Salvar valores antigos antes de atualizar
            const oldCategory = lastCategoryRef.current;
            const oldSearchTerm = lastSearchTermRef.current;
            
            // Atualizar referências antes de buscar
            lastCategoryRef.current = selectedCategory;
            lastSearchTermRef.current = searchTerm;
            lastBarcodeRef.current = barcode;
            lastInternalCodeRef.current = internalCode;
            lastPageSizeRef.current = pageSize;
            lastSomenteComPromocoesRef.current = somenteComPromocoes;
            lastTiposUnidadeRef.current = tiposUnidade;

            // Se mudou, resetar para página 1 e marcar para limpar produtos
            shouldClearProductsRef.current = true;
            setCurrentPage(1);
            pageHistoryRef.current = [1];
            
            // OTIMIZAÇÃO: Quando apenas pageSize mudar (não categoria/busca), 
            // NÃO limpar o cache - permitir reutilização de produtos já carregados
            // O cache será adaptado automaticamente para o novo pageSize
            if (pageSizeChanged && !categoryChanged && !searchChanged && !barcodeChanged && !internalCodeChanged && !somenteComPromocoesChanged && !tiposUnidadeChanged) {
                // Não limpar cache - produtos do store serão reutilizados
                // A função buscarProdutos tentará usar produtos do store primeiro
            } else if (categoryChanged || searchChanged || barcodeChanged || internalCodeChanged || somenteComPromocoesChanged || tiposUnidadeChanged) {
                // Se categoria, busca, filtro de promoção ou tipos de unidade mudou, limpar cache
                pageCacheRef.current.clear();
            }
            
            buscarProdutos(1);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedCategory, searchTerm, barcode, internalCode, somenteComPromocoes, tiposUnidade, pageSize, autoSearch]);
    // Removido buscarProdutos das dependências - ele já usa os valores corretos via closure

    const goToNextPage = useCallback(() => {
        if (hasNextPage && !loading) {
            const nextPage = currentPage + 1;
            setCurrentPage(nextPage);
            pageHistoryRef.current.push(nextPage);
            buscarProdutos(nextPage);
        }
    }, [hasNextPage, loading, currentPage, buscarProdutos]);

    const goToPreviousPage = useCallback(() => {
        if (hasPreviousPage && !loading) {
            const prevPage = currentPage - 1;
            setCurrentPage(prevPage);
            buscarProdutos(prevPage);
        }
    }, [hasPreviousPage, loading, currentPage, buscarProdutos]);

    const refreshCurrentPage = useCallback(() => {
        if (!loading) {
            // Forçar refresh ignorando cache
            buscarProdutos(currentPage, true);
        }
    }, [currentPage, loading, buscarProdutos]);

    return {
        currentPage,
        totalPages: 1, // Não usamos mais totalPages
        totalItems: produtosBuscados.length,
        loading,
        error,
        produtosBuscados,
        vinculacoesProdutos, // Mapa de produtoId -> vinculadoATela
        goToPage: () => {}, // Não usado mais
        goToNextPage,
        goToPreviousPage,
        refreshCurrentPage,
        hasNextPage,
        hasPreviousPage,
        triggerSearch // Função para buscar manualmente
    };
};

